// CurrencyConverterWidget.jsx - FIXED FOR WINDOWS/DESKTOP
// Uses flag images instead of emoji flags for cross-platform compatibility

import React, { useState, useEffect } from 'react';
import { ArrowLeftRight, TrendingUp, Zap, Shield, Info, CreditCard, Gift, Sparkles } from 'lucide-react';

// FALLBACK EXCHANGE RATES
const FALLBACK_RATES = {
  'USD': 1, 'EUR': 0.92, 'GBP': 0.79, 'NGN': 1443.19, 'GHS': 15.85,
  'KES': 129.50, 'ZAR': 18.25, 'INR': 83.12, 'CAD': 1.35, 'AUD': 1.52,
  'JPY': 149.50, 'CNY': 7.24, 'BRL': 4.97, 'MXN': 17.08, 'AED': 3.67,
  'SAR': 3.75, 'CHF': 0.85, 'SEK': 10.35, 'NOK': 10.58, 'DKK': 6.87,
  'PLN': 3.98, 'TRY': 32.25, 'THB': 33.75, 'MYR': 4.48, 'SGD': 1.33,
  'IDR': 15780, 'PHP': 56.25, 'VND': 24650, 'PKR': 278.50, 'BDT': 110.25,
  'EGP': 48.90, 'MAD': 9.98, 'KWD': 0.31, 'QAR': 3.64, 'ILS': 3.68,
  'CLP': 965.50, 'COP': 3925, 'ARS': 1015, 'PEN': 3.72, 'UYU': 39.25
};

// Helper component for flag images
const FlagIcon = ({ countryCode, size = "w-8 h-6" }) => {
  const code = countryCode.toLowerCase();
  return (
    <img 
      src={`https://flagcdn.com/${code}.svg`}
      alt={countryCode}
      className={`${size} rounded shadow-sm`}
      onError={(e) => {
        e.target.onerror = null;
        e.target.src = `https://cdn.jsdelivr.net/npm/country-flag-emoji-json@2.0.0/dist/images/${countryCode}.svg`;
      }}
    />
  );
};

// All supported currencies with country codes
const CURRENCIES = [
  { code: 'EUR', name: 'Euro', country: 'at' }, // Austria
  { code: 'USD', name: 'US Dollar', country: 'us' },
  { code: 'BDT', name: 'Bangladeshi Taka', country: 'bd' },
  { code: 'EUR', name: 'Euro', country: 'be' },
  { code: 'XOF', name: 'West African CFA Franc', country: 'bj' },
  { code: 'BRL', name: 'Brazilian Real', country: 'br' },
  { code: 'XOF', name: 'West African CFA Franc', country: 'bf' },
  { code: 'KHR', name: 'Cambodian Riel', country: 'kh' },
  { code: 'XAF', name: 'Central African CFA Franc', country: 'cm' },
  { code: 'XAF', name: 'Central African CFA Franc', country: 'cf' },
  { code: 'XAF', name: 'Central African CFA Franc', country: 'td' },
  { code: 'CNY', name: 'Chinese Yuan', country: 'cn' },
  { code: 'COP', name: 'Colombian Peso', country: 'co' },
  { code: 'KMF', name: 'Comorian Franc', country: 'km' },
  { code: 'DOP', name: 'Dominican Peso', country: 'do' },
  { code: 'CDF', name: 'Congolese Franc', country: 'cd' },
  { code: 'EGP', name: 'Egyptian Pound', country: 'eg' },
  { code: 'USD', name: 'US Dollar', country: 'sv' },
  { code: 'ETB', name: 'Ethiopian Birr', country: 'et' },
  { code: 'XAF', name: 'Central African CFA Franc', country: 'gq' },
  { code: 'FJD', name: 'Fijian Dollar', country: 'fj' },
  { code: 'EUR', name: 'Euro', country: 'fr' },
  { code: 'GMD', name: 'Gambian Dalasi', country: 'gm' },
  { code: 'XAF', name: 'Central African CFA Franc', country: 'ga' },
  { code: 'EUR', name: 'Euro', country: 'de' },
  { code: 'GHS', name: 'Ghanaian Cedi', country: 'gh' },
  { code: 'EUR', name: 'Euro', country: 'gr' },
  { code: 'GTQ', name: 'Guatemalan Quetzal', country: 'gt' },
  { code: 'GNF', name: 'Guinean Franc', country: 'gn' },
  { code: 'HTG', name: 'Haitian Gourde', country: 'ht' },
  { code: 'HNL', name: 'Honduran Lempira', country: 'hn' },
  { code: 'INR', name: 'Indian Rupee', country: 'in' },
  { code: 'IDR', name: 'Indonesian Rupiah', country: 'id' },
  { code: 'EUR', name: 'Euro', country: 'ie' },
  { code: 'EUR', name: 'Euro', country: 'it' },
  { code: 'XOF', name: 'West African CFA Franc', country: 'ci' },
  { code: 'JMD', name: 'Jamaican Dollar', country: 'jm' },
  { code: 'JOD', name: 'Jordanian Dinar', country: 'jo' },
  { code: 'KES', name: 'Kenyan Shilling', country: 'ke' },
  { code: 'LBP', name: 'Lebanese Pound', country: 'lb' },
  { code: 'LRD', name: 'Liberian Dollar', country: 'lr' },
  { code: 'MGA', name: 'Malagasy Ariary', country: 'mg' },
  { code: 'XOF', name: 'West African CFA Franc', country: 'ml' },
  { code: 'MRU', name: 'Mauritanian Ouguiya', country: 'mr' },
  { code: 'MXN', name: 'Mexican Peso', country: 'mx' },
  { code: 'MAD', name: 'Moroccan Dirham', country: 'ma' },
  { code: 'MZN', name: 'Mozambican Metical', country: 'mz' },
  { code: 'NPR', name: 'Nepalese Rupee', country: 'np' },
  { code: 'EUR', name: 'Euro', country: 'nl' },
  { code: 'NGN', name: 'Nigerian Naira', country: 'ng' },
  { code: 'NOK', name: 'Norwegian Krone', country: 'no' },
  { code: 'PKR', name: 'Pakistani Rupee', country: 'pk' },
  { code: 'PEN', name: 'Peruvian Sol', country: 'pe' },
  { code: 'PHP', name: 'Philippine Peso', country: 'ph' },
  { code: 'PLN', name: 'Polish Złoty', country: 'pl' },
  { code: 'EUR', name: 'Euro', country: 'pt' },
  { code: 'XAF', name: 'Central African CFA Franc', country: 'cg' },
  { code: 'RON', name: 'Romanian Leu', country: 'ro' },
  { code: 'RWF', name: 'Rwandan Franc', country: 'rw' },
  { code: 'XOF', name: 'West African CFA Franc', country: 'sn' },
  { code: 'LKR', name: 'Sri Lankan Rupee', country: 'lk' },
  { code: 'ZAR', name: 'South African Rand', country: 'za' },
  { code: 'KRW', name: 'South Korean Won', country: 'kr' },
  { code: 'EUR', name: 'Euro', country: 'es' },
  { code: 'SEK', name: 'Swedish Krona', country: 'se' },
  { code: 'TZS', name: 'Tanzanian Shilling', country: 'tz' },
  { code: 'THB', name: 'Thai Baht', country: 'th' },
  { code: 'XOF', name: 'West African CFA Franc', country: 'tg' },
  { code: 'TND', name: 'Tunisian Dinar', country: 'tn' },
  { code: 'TRY', name: 'Turkish Lira', country: 'tr' },
  { code: 'UGX', name: 'Ugandan Shilling', country: 'ug' },
  { code: 'GBP', name: 'British Pound', country: 'gb' },
  { code: 'VND', name: 'Vietnamese Dong', country: 'vn' },
  { code: 'ZMW', name: 'Zambian Kwacha', country: 'zm' },
  { code: 'ZWL', name: 'Zimbabwean Dollar', country: 'zw' },
];

// ALL AVAILABLE COUNTRIES (with currencies)
const AVAILABLE_COUNTRIES = [
  { name: 'Austria', code: 'at', currency: 'EUR' },
  { name: 'Bangladesh', code: 'bd', currency: 'BDT' },
  { name: 'Belgium', code: 'be', currency: 'EUR' },
  { name: 'Benin', code: 'bj', currency: 'XOF' },
  { name: 'Brazil', code: 'br', currency: 'BRL' },
  { name: 'Burkina Faso', code: 'bf', currency: 'XOF' },
  { name: 'Cambodia', code: 'kh', currency: 'KHR' },
  { name: 'Cameroon', code: 'cm', currency: 'XAF' },
  { name: 'Central African Republic', code: 'cf', currency: 'XAF' },
  { name: 'Chad', code: 'td', currency: 'XAF' },
  { name: 'China', code: 'cn', currency: 'CNY' },
  { name: 'Colombia', code: 'co', currency: 'COP' },
  { name: 'Comoros', code: 'km', currency: 'KMF' },
  { name: 'Dominican Rep.', code: 'do', currency: 'DOP' },
  { name: 'DR Congo', code: 'cd', currency: 'CDF' },
  { name: 'Egypt', code: 'eg', currency: 'EGP' },
  { name: 'El Salvador', code: 'sv', currency: 'USD' },
  { name: 'Ethiopia', code: 'et', currency: 'ETB' },
  { name: 'Equatorial Guinea', code: 'gq', currency: 'XAF' },
  { name: 'Fiji', code: 'fj', currency: 'FJD' },
  { name: 'France', code: 'fr', currency: 'EUR' },
  { name: 'The Gambia', code: 'gm', currency: 'GMD' },
  { name: 'Gabon', code: 'ga', currency: 'XAF' },
  { name: 'Germany', code: 'de', currency: 'EUR' },
  { name: 'Ghana', code: 'gh', currency: 'GHS' },
  { name: 'Greece', code: 'gr', currency: 'EUR' },
  { name: 'Guatemala', code: 'gt', currency: 'GTQ' },
  { name: 'Guinea', code: 'gn', currency: 'GNF' },
  { name: 'Haiti', code: 'ht', currency: 'HTG' },
  { name: 'Honduras', code: 'hn', currency: 'HNL' },
  { name: 'India', code: 'in', currency: 'INR' },
  { name: 'Indonesia', code: 'id', currency: 'IDR' },
  { name: 'Ireland', code: 'ie', currency: 'EUR' },
  { name: 'Italy', code: 'it', currency: 'EUR' },
  { name: 'Ivory Coast', code: 'ci', currency: 'XOF' },
  { name: 'Jamaica', code: 'jm', currency: 'JMD' },
  { name: 'Jordan', code: 'jo', currency: 'JOD' },
  { name: 'Kenya', code: 'ke', currency: 'KES' },
  { name: 'Lebanon', code: 'lb', currency: 'LBP' },
  { name: 'Liberia', code: 'lr', currency: 'LRD' },
  { name: 'Madagascar', code: 'mg', currency: 'MGA' },
  { name: 'Mali', code: 'ml', currency: 'XOF' },
  { name: 'Mauritania', code: 'mr', currency: 'MRU' },
  { name: 'Mexico', code: 'mx', currency: 'MXN' },
  { name: 'Morocco', code: 'ma', currency: 'MAD' },
  { name: 'Mozambique', code: 'mz', currency: 'MZN' },
  { name: 'Nepal', code: 'np', currency: 'NPR' },
  { name: 'Netherlands', code: 'nl', currency: 'EUR' },
  { name: 'Nigeria', code: 'ng', currency: 'NGN' },
  { name: 'Norway', code: 'no', currency: 'NOK' },
  { name: 'Pakistan', code: 'pk', currency: 'PKR' },
  { name: 'Peru', code: 'pe', currency: 'PEN' },
  { name: 'Philippines', code: 'ph', currency: 'PHP' },
  { name: 'Poland', code: 'pl', currency: 'PLN' },
  { name: 'Portugal', code: 'pt', currency: 'EUR' },
  { name: 'Rep. Congo', code: 'cg', currency: 'XAF' },
  { name: 'Romania', code: 'ro', currency: 'RON' },
  { name: 'Rwanda', code: 'rw', currency: 'RWF' },
  { name: 'Senegal', code: 'sn', currency: 'XOF' },
  { name: 'Sri Lanka', code: 'lk', currency: 'LKR' },
  { name: 'South Africa', code: 'za', currency: 'ZAR' },
  { name: 'South Korea', code: 'kr', currency: 'KRW' },
  { name: 'Spain', code: 'es', currency: 'EUR' },
  { name: 'Sweden', code: 'se', currency: 'SEK' },
  { name: 'Tanzania', code: 'tz', currency: 'TZS' },
  { name: 'Thailand', code: 'th', currency: 'THB' },
  { name: 'Togo', code: 'tg', currency: 'XOF' },
  { name: 'Tunisia', code: 'tn', currency: 'TND' },
  { name: 'Turkey', code: 'tr', currency: 'TRY' },
  { name: 'Uganda', code: 'ug', currency: 'UGX' },
  { name: 'United Kingdom', code: 'gb', currency: 'GBP' },
  { name: 'Vietnam', code: 'vn', currency: 'VND' },
  { name: 'Zambia', code: 'zm', currency: 'ZMW' },
  { name: 'Zimbabwe', code: 'zw', currency: 'ZWL' },
];

const CurrencyConverterWidget = ({ onGetStarted, onActivateCard }) => {
  const [fromCurrency, setFromCurrency] = useState('USD');
  const [toCurrency, setToCurrency] = useState('EUR');
  const [amount, setAmount] = useState('');
  const [convertedAmount, setConvertedAmount] = useState('0');
  const [exchangeRate, setExchangeRate] = useState(0);
  const [loading, setLoading] = useState(false);
  const [showFromDropdown, setShowFromDropdown] = useState(false);
  const [showToDropdown, setShowToDropdown] = useState(false);
  const [fromSearch, setFromSearch] = useState('');
  const [toSearch, setToSearch] = useState('');

  const getFromCurrencyDetails = () => CURRENCIES.find(c => c.code === fromCurrency) || CURRENCIES[0];
  const getToCurrencyDetails = () => CURRENCIES.find(c => c.code === toCurrency) || CURRENCIES[1];

  const filteredFromCurrencies = CURRENCIES.filter(c => 
    c.code.toLowerCase().includes(fromSearch.toLowerCase()) ||
    c.name.toLowerCase().includes(fromSearch.toLowerCase())
  );

  const filteredToCurrencies = CURRENCIES.filter(c => 
    c.code.toLowerCase().includes(toSearch.toLowerCase()) ||
    c.name.toLowerCase().includes(toSearch.toLowerCase())
  );

  const convertWithFallback = (amt, from, to) => {
    const fromRate = FALLBACK_RATES[from] || 1;
    const toRate = FALLBACK_RATES[to] || 1;
    const inUSD = amt / fromRate;
    const converted = inUSD * toRate;
    const rate = toRate / fromRate;
    return { convertedAmount: converted, exchangeRate: rate };
  };

  useEffect(() => {
    const convertCurrency = () => {
      const amt = parseFloat(amount);
      if (!amount || amt <= 0 || isNaN(amt)) {
        setConvertedAmount('0');
        const result = convertWithFallback(1, fromCurrency, toCurrency);
        setExchangeRate(result.exchangeRate);
        return;
      }
      setLoading(true);
      const result = convertWithFallback(amt, fromCurrency, toCurrency);
      setConvertedAmount(result.convertedAmount.toFixed(2));
      setExchangeRate(result.exchangeRate);
      setLoading(false);
    };
    const debounceTimer = setTimeout(convertCurrency, 100);
    return () => clearTimeout(debounceTimer);
  }, [amount, fromCurrency, toCurrency]);

  const handleSwapCurrencies = () => {
    const temp = fromCurrency;
    setFromCurrency(toCurrency);
    setToCurrency(temp);
  };

  const handleAmountChange = (e) => {
    const value = e.target.value;
    if (value === '' || /^\d*\.?\d*$/.test(value)) {
      setAmount(value);
    }
  };

  const formatNumber = (num) => {
    const parsed = parseFloat(num);
    if (isNaN(parsed)) return '0.00';
    return parsed.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  return (
    <div className="w-full max-w-7xl mx-auto px-4 py-16">
      {/* Header */}
      <div className="text-center mb-12">
        <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
          Our <span className="text-blue-400">Core Features</span>
        </h2>
        <p className="text-xl text-purple-200">
          Check exchange rates and activate your crypto gift card - all in one place
        </p>
      </div>

      {/* Side by Side: Currency Converter + Crypto Card */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-16">
        
        {/* LEFT: Currency Converter */}
        <div className="bg-gradient-to-br from-purple-900/90 to-blue-900/90 backdrop-blur-xl rounded-3xl shadow-2xl p-6 border-2 border-purple-500/30">
          <h3 className="text-2xl font-bold text-white mb-6 text-center">
            💱 Currency Converter
          </h3>
          
          <div className="space-y-4">
            {/* You Send */}
            <div className="space-y-2">
              <label className="text-purple-200 font-medium text-sm">You send</label>
              <div className="bg-black/30 rounded-xl p-3 border-2 border-purple-500/30 focus-within:border-blue-400 transition-all">
                <input
                  type="text"
                  value={amount}
                  onChange={handleAmountChange}
                  placeholder="Enter amount"
                  className="w-full bg-transparent text-white text-2xl font-bold outline-none placeholder-gray-500"
                />
              </div>

              {/* From Currency */}
              <div className="relative">
                <button
                  onClick={() => { setShowFromDropdown(!showFromDropdown); setShowToDropdown(false); }}
                  className="w-full bg-black/30 rounded-xl p-3 border-2 border-purple-500/30 hover:border-blue-400 transition-all flex items-center justify-between"
                >
                  <div className="flex items-center space-x-3">
                    <FlagIcon countryCode={getFromCurrencyDetails()?.country} />
                    <div className="text-left">
                      <div className="text-white font-bold">{fromCurrency}</div>
                      <div className="text-purple-300 text-xs">{getFromCurrencyDetails()?.name}</div>
                    </div>
                  </div>
                  <svg className={`w-4 h-4 text-purple-300 transition-transform ${showFromDropdown ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {showFromDropdown && (
                  <div className="absolute z-50 w-full mt-2 bg-gray-900 rounded-xl border-2 border-purple-500/50 shadow-2xl max-h-72 overflow-y-auto">
                    <div className="p-2 border-b border-purple-500/30">
                      <input type="text" value={fromSearch} onChange={(e) => setFromSearch(e.target.value)} placeholder="Search..." className="w-full bg-black/50 text-white px-3 py-2 rounded-lg outline-none text-sm" />
                    </div>
                    <div className="p-2">
                      {filteredFromCurrencies.map((currency) => (
                        <button key={currency.code} onClick={() => { setFromCurrency(currency.code); setShowFromDropdown(false); setFromSearch(''); }} className="w-full flex items-center space-x-3 p-2 rounded-lg hover:bg-purple-700/50 transition-colors">
                          <FlagIcon countryCode={currency.country} size="w-6 h-4" />
                          <div className="flex-1 text-left">
                            <div className="text-white font-bold text-sm">{currency.code}</div>
                            <div className="text-purple-300 text-xs">{currency.name}</div>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Swap Button */}
            <div className="flex justify-center">
              <button onClick={handleSwapCurrencies} className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 p-3 rounded-full shadow-xl transition-all transform hover:scale-110 hover:rotate-180 duration-300">
                <ArrowLeftRight className="text-white" size={20} />
              </button>
            </div>

            {/* They Receive */}
            <div className="space-y-2">
              <label className="text-purple-200 font-medium text-sm">They receive</label>
              <div className="bg-gradient-to-br from-green-600/20 to-blue-600/20 rounded-xl p-3 border-2 border-green-500/50">
                <div className="text-white text-2xl font-bold">
                  {loading ? <span className="text-lg">Converting...</span> : formatNumber(convertedAmount)}
                </div>
              </div>

              {/* To Currency */}
              <div className="relative">
                <button
                  onClick={() => { setShowToDropdown(!showToDropdown); setShowFromDropdown(false); }}
                  className="w-full bg-black/30 rounded-xl p-3 border-2 border-purple-500/30 hover:border-blue-400 transition-all flex items-center justify-between"
                >
                  <div className="flex items-center space-x-3">
                    <FlagIcon countryCode={getToCurrencyDetails()?.country} />
                    <div className="text-left">
                      <div className="text-white font-bold">{toCurrency}</div>
                      <div className="text-purple-300 text-xs">{getToCurrencyDetails()?.name}</div>
                    </div>
                  </div>
                  <svg className={`w-4 h-4 text-purple-300 transition-transform ${showToDropdown ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {showToDropdown && (
                  <div className="absolute z-50 w-full mt-2 bg-gray-900 rounded-xl border-2 border-purple-500/50 shadow-2xl max-h-72 overflow-y-auto">
                    <div className="p-2 border-b border-purple-500/30">
                      <input type="text" value={toSearch} onChange={(e) => setToSearch(e.target.value)} placeholder="Search..." className="w-full bg-black/50 text-white px-3 py-2 rounded-lg outline-none text-sm" />
                    </div>
                    <div className="p-2">
                      {filteredToCurrencies.map((currency) => (
                        <button key={currency.code} onClick={() => { setToCurrency(currency.code); setShowToDropdown(false); setToSearch(''); }} className="w-full flex items-center space-x-3 p-2 rounded-lg hover:bg-purple-700/50 transition-colors">
                          <FlagIcon countryCode={currency.country} size="w-6 h-4" />
                          <div className="flex-1 text-left">
                            <div className="text-white font-bold text-sm">{currency.code}</div>
                            <div className="text-purple-300 text-xs">{currency.name}</div>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Rate Info */}
            <div className="bg-black/20 rounded-xl p-3">
              <div className="flex items-center justify-between mb-1">
                <span className="text-purple-200 text-sm">Exchange rate:</span>
                <span className="text-white font-bold">1 {fromCurrency} = {formatNumber(exchangeRate)} {toCurrency}</span>
              </div>
              <div className="text-xs text-green-400 flex items-center space-x-1">
                <Shield size={12} />
                <span>No fees • Best rates</span>
              </div>
            </div>

            {/* CTA */}
            <button onClick={onGetStarted} className="w-full bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white font-bold py-4 rounded-xl transition-all transform hover:scale-105 shadow-xl flex items-center justify-center space-x-2">
              <Zap size={20} />
              <span>Send Money Now</span>
            </button>
          </div>
        </div>

        {/* RIGHT: Crypto Gift Card */}
        <div className="bg-gradient-to-br from-orange-900/90 to-pink-900/90 backdrop-blur-xl rounded-3xl shadow-2xl p-6 border-2 border-orange-500/30">
          <h3 className="text-2xl font-bold text-white mb-6 text-center">
            🎁 Crypto Gift Card
          </h3>
          
          {/* Card Display */}
          <div className="relative mb-6">
            <div className="bg-gradient-to-br from-purple-600 via-pink-600 to-orange-600 rounded-2xl p-6 shadow-2xl transform hover:scale-105 transition-all duration-300 relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent animate-shimmer"></div>
              
              <div className="relative z-10">
                <div className="flex justify-between items-start mb-8">
                  <div>
                    <img 
                      src="/zanors-logo-full.png" 
                      alt="Zanors" 
                      className="h-14 mb-1 opacity-90" 
                      style={{ mixBlendMode: 'screen' }}
                    />
                    <div className="text-white/70 text-xs">Crypto Prepaid Card</div>
                  </div>
                  <CreditCard className="text-white/80" size={40} />
                </div>

                <div className="mb-6">
                  <div className="text-white/70 text-xs mb-1">Card Number</div>
                  <div className="text-white font-mono text-xl tracking-wider">•••• •••• •••• 8472</div>
                </div>

                <div className="flex justify-between items-end">
                  <div>
                    
                    <div className="text-white font-semibold">USD Balance</div>
                    <div className="text-white/70 text-xs mb-1">$0.00</div>
                  </div>
                  <div className="flex space-x-4">
                    <div className="text-center">
                      <div className="text-white/70 text-xs">BTC</div>
                      <div className="text-white font-bold">0.00000000</div>
                    </div>
                    <div className="text-center">
                      <div className="text-white/70 text-xs">ETH</div>
                      <div className="text-white font-bold">0.00000000</div>
                    </div>
                  </div>
                </div>

                <div className="absolute top-2 right-2">
                  <Sparkles className="text-yellow-300 animate-pulse" size={24} />
                </div>
              </div>
            </div>
          </div>

          {/* Features */}
          <div className="space-y-3 mb-6">
            <div className="flex items-center space-x-3 bg-black/20 rounded-lg p-3">
              <div className="bg-orange-600 rounded-full p-2">
                <Gift className="text-white" size={16} />
              </div>
              <div className="text-white text-sm">Perfect gift for crypto enthusiasts</div>
            </div>
            <div className="flex items-center space-x-3 bg-black/20 rounded-lg p-3">
              <div className="bg-pink-600 rounded-full p-2">
                <Shield className="text-white" size={16} />
              </div>
              <div className="text-white text-sm">Secure & ready to use instantly</div>
            </div>
            <div className="flex items-center space-x-3 bg-black/20 rounded-lg p-3">
              <div className="bg-purple-600 rounded-full p-2">
                <Zap className="text-white" size={16} />
              </div>
              <div className="text-white text-sm">Pre-loaded BTC, ETH & more</div>
            </div>
          </div>

          {/* Activate Button */}
          <button 
            onClick={onActivateCard}
            className="w-full bg-gradient-to-r from-orange-600 to-pink-600 hover:from-orange-700 hover:to-pink-700 text-white font-bold py-4 rounded-xl transition-all transform hover:scale-105 shadow-xl flex items-center justify-center space-x-2"
          >
            <CreditCard size={20} />
            <span>Activate Your Card Here</span>
          </button>
        </div>
      </div>

      {/* Countries Grid */}
      <div className="bg-gradient-to-br from-gray-900/90 to-purple-900/90 backdrop-blur-xl rounded-3xl shadow-2xl p-8 border-2 border-purple-500/30">
        <div className="text-center mb-8">
          <h3 className="text-3xl font-bold text-white mb-3">
            Send is currently available in these countries
          </h3>
          <p className="text-purple-300 text-lg">
            More countries coming soon.
          </p>
        </div>

        {/* Countries Grid - 8 per row */}
        <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-4">
          {AVAILABLE_COUNTRIES.map((country) => (
            <div
              key={country.name}
              className="bg-black/30 rounded-lg p-3 flex flex-col items-center justify-center hover:bg-purple-700/30 transition-all cursor-pointer group"
            >
              <FlagIcon countryCode={country.code} size="w-12 h-9" />
              <span className="text-white text-xs text-center font-medium mt-2">{country.name}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default CurrencyConverterWidget;